import { Component, inject } from "@angular/core";
import { FormBuilder } from "@angular/forms";
import { finalize } from "rxjs/operators";
import { FormControlBase } from "src/app/common-components/form-controls/form-control-base";
import { CstResponsivePopupComponentBase } from "src/app/common-components/resp-popup";
import {
  CstJsonPatchDocumentOfNoteFacilityUpdateCommand,
  NoteFacilityCreateCommand,
  NoteFacilityPatchCommand,
  NotesClientProvider,
  NotesFacilitySyncCommand,
  OperationOfNoteFacilityUpdateCommand,
  ValidationResult,
} from "src/app/web-api/web-api";
import { WawiApiNoteFacilityUpdateFormGroup } from "./form/note-facility-update-form.component";

export interface IWawiApiNoteFacilityUpdateDialogData {
  noteID: number;
}

@Component({
    templateUrl: "./note-facility-update-dialog.component.html",
    styleUrls: ["./note-facility-update-dialog.component.scss"],
    standalone: false
})
export class WawiApiNoteFacilityUpdateDialogComponent extends CstResponsivePopupComponentBase<IWawiApiNoteFacilityUpdateDialogData> {
  private _client = inject(NotesClientProvider);

  public loading = false;
  public form: WawiApiNoteFacilityUpdateFormGroup;

  public constructor() {
    const fb = inject(FormBuilder);

    super();
    this.form = new WawiApiNoteFacilityUpdateFormGroup(fb);
  }

  onInitialised() {
    if (this.data.noteID) {
      this.form.noteID.setValue(this.data.noteID);
      this.form.noteID.disable();
      this._client.notesFacilityGetOne(this.data.noteID).subscribe((result) => {
        this.form.note.setValue(result.note);
        this.form.displayLevelMobile.setValue(result.displayLevelMobile);
        this.form.acknowledgedOnMobile.setValue(result.acknowledgedOnMobile);
        this.form.displayLevel.setValue(result.displayLevel);
      });
    }
  }

  private createPatchOperation(op:string, path:string, value: string):CstJsonPatchDocumentOfNoteFacilityUpdateCommand{
    const operation = new OperationOfNoteFacilityUpdateCommand();
    operation.op = op;
    operation.path = "/" + path;
    operation.value = value;
    return operation;
  }

  private createPatchCommand(): NoteFacilityPatchCommand{
    const patchCmd = new NoteFacilityPatchCommand();
    patchCmd.id = this.form.noteID.value;
    patchCmd.patch = new CstJsonPatchDocumentOfNoteFacilityUpdateCommand();
    patchCmd.patch.operations = new Array<OperationOfNoteFacilityUpdateCommand>();
    patchCmd.patch.operations.push(this.createPatchOperation("replace", "note", this.form.note.value));
    patchCmd.patch.operations.push(this.createPatchOperation("replace", "displayLevelMobile", this.form.displayLevelMobile.value));
    patchCmd.patch.operations.push(this.createPatchOperation("replace", "acknowledgedOnMobile", this.form.acknowledgedOnMobile.value));
    patchCmd.patch.operations.push(this.createPatchOperation("replace", "displayLevel", this.form.displayLevel.value));
    return patchCmd;
  }

  private createSyncCommand(): NotesFacilitySyncCommand{
    const syncCmd = new NotesFacilitySyncCommand();
    syncCmd.createCommands = new Array<NoteFacilityCreateCommand>();
    syncCmd.patchCommands = new Array<NoteFacilityPatchCommand>();
    return syncCmd;
  }

  public OnAccept() {
    if (!this.form.valid) {
      return;
    }
    const patchCmd = this.createPatchCommand();
    const syncCmd = this.createSyncCommand();
    syncCmd.patchCommands.push(patchCmd);
    this.loading = true;
    this._client
      .notesFacilitySync(syncCmd)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (result) => {
          this.close(result.updated[0]);
        },
        error: (error) => {
          if (error instanceof ValidationResult) {
            for (const prop of error.properties) {
              prop.property = prop.property.replace(
                /updatecommands\[[0-9]\]\./gim,
                ""
              );
            }
            setTimeout(
              () =>
                FormControlBase.ApplyValidationResultToForm(this.form, error),
              200
            );
            return;
          }
        },
      });
  }

  public OnCancel() {
    this.close(null);
  }
}
